import * as React from 'react';

import {
    ApplicationHelpType,
    LookupDataEnums,
    SystemGeneratedDocumentsTypesEnum,
    UserRightsEnum,
    ZoneContextType,
} from '../../../utilities/Constants';
import { ILookupModel, IMultiSelectOption } from '../../../interfaces/ILookup';
import {
    IPackageSettlementBaseModel,
    IPackageSettlementFirmModel,
} from '../../../interfaces/ICase';
import { Link, useLocation } from 'react-router-dom';
import { Offcanvas, Stack } from 'react-bootstrap';

import { ApiRoutes } from '../../../utilities/ApiRoutes';
import Authorization from '../../../stores/Authorization';
import { CheckmateDialog } from '../../../components/shared/dialog';
import Common from '../../../stores/Common';
import { DisplayMessages } from '../../../utilities/DisplayMessages';
import { DocumentTitle } from '../../../components/shared/DocumentTitle';
import { IPackageSettlementSearchModel } from '../../../interfaces/IPackageSettlement';
import { IReportDateFilterModel } from '../../../interfaces/Report/IReport';
import { IUserModel } from '../../../interfaces/IUser';
import { IValidation } from '../../../interfaces/IError';
import { Loader } from '../../../components/shared/Loader';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import { PackageListAppliedFilters } from './PackageListAppliedFilters';
import { PackageListSearchFilters } from './PackageListSearchFilters';
import { PageWithAffixedHeader } from '../../../shared/PageWithAffixedHeader';
import Sort from '../../../stores/Sort';
import { SortableHeader } from '../../../components/shared/SortableHeader';
import ValidateUtils from '../../../shared/validations';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useGetLookupData } from '../../../shared/react-query-hooks/useGetLookupData';
import { useGetPlaintiffsFirms } from '../../../shared/react-query-hooks/useGetPlaintiffsFirms';
import { useZoneContext } from '../../../contexts/useZoneContext';

interface IPackageSettlementListWrapperProps {
    refreshIdentifier?: string;
    statuses?: IMultiSelectOption[] | undefined;
    plaintiffsFirms?: IMultiSelectOption[] | undefined;
    zoneContext?: ZoneContextType;
}
interface IPackageSettlementListProps extends IPackageSettlementListWrapperProps {
    user: IUserModel;
}

interface IPackageSettlementListState {
    pendingResponse: boolean;
    validation: IValidation;
    packageSettlementList: IPackageSettlementBaseModel[];
    startDateFilter?: string;
    endDateFilter?: string;
    selectedStatuses: IMultiSelectOption[];
    selectedPlaintiffsFirms: IMultiSelectOption[];
    selectedPackageSettlementGuidToDelete?: string;
    showConfirmDeleteDialog?: boolean;
    showFilterPane?: boolean;
}

function lookupItemToMultiSelectOptionMapper(lookupItem: ILookupModel) {
    const value =
        lookupItem.guid === '00000000-0000-0000-0000-000000000000' || !lookupItem.guid
            ? lookupItem.id
            : lookupItem.guid;

    return {
        id: lookupItem.id,
        label: lookupItem.displayName,
        value,
    } as IMultiSelectOption;
}

export function PackageSettlementListWrapper(props: IPackageSettlementListWrapperProps) {
    const packageDealStatuses = useGetLookupData(LookupDataEnums.PackageSettlementStatusTypes);
    const plaintiffsFirms = useGetPlaintiffsFirms();
    const { user } = useAuthContext();
    const zoneContext = useZoneContext();
    const { key } = useLocation();

    if (packageDealStatuses.isFetching || plaintiffsFirms.isFetching) {
        return <Loader />;
    }

    return (
        <PackageSettlementList
            {...props}
            statuses={packageDealStatuses.data?.map(lookupItemToMultiSelectOptionMapper) ?? []}
            plaintiffsFirms={plaintiffsFirms.data?.map(lookupItemToMultiSelectOptionMapper) ?? []}
            zoneContext={zoneContext}
            user={user}
            key={key}
        />
    );
}

const pageTitle = 'Package Deals';

export class PackageSettlementList extends React.Component<
    IPackageSettlementListProps,
    IPackageSettlementListState
> {
    constructor(props: IPackageSettlementListProps) {
        super(props);

        this.state = {
            pendingResponse: false,
            packageSettlementList: [],
            selectedStatuses: [],
            selectedPlaintiffsFirms: [],
            validation: {},
        };
    }

    componentDidMount() {
        if (
            !Authorization.isAuthorizedToRoute(
                LocalRoutes.PackageSettlements,
                this.props.user,
                this.props.zoneContext?.zoneGuid
            )
        ) {
            window.location.assign(LocalRoutes.AccessDenied);
        }
        this.loadPackageSettlementList();
    }

    componentDidUpdate(
        prevProps: IPackageSettlementListProps,
        prevState: IPackageSettlementListState
    ) {
        if (
            this.props.refreshIdentifier &&
            prevProps.refreshIdentifier != this.props.refreshIdentifier
        ) {
            this.setState(
                { startDateFilter: undefined, endDateFilter: undefined, selectedStatuses: [] },
                this.loadPackageSettlementList
            );
        }

        if (prevState.pendingResponse && this.state.pendingResponse == false) {
            this.setState({ showFilterPane: false });
        }
    }

    loadPackageSettlementList = async () => {
        if (this.props.zoneContext) {
            this.setState({ pendingResponse: true });

            const selectedPlaintiffsFirms = this.state.selectedPlaintiffsFirms.map(
                (filter) => filter.value
            );

            const searchModel = {
                startDate: this.state.startDateFilter,
                endDate: this.state.endDateFilter,
                statusIds: this.state.selectedStatuses?.map((filter) => filter.id),
                plaintiffsFirmIds: selectedPlaintiffsFirms,
            };

            const res =
                await this.props.zoneContext.zoneApiClient.getPackageSettlements(searchModel);

            if (res.httpResponse.status == 401) {
                window.location.reload();
                return;
            }
            if (res.errorMessage) {
                this.setState({
                    validation: ValidateUtils.parseErrors(res.errors, res.errorMessage),
                });
                return;
            }
            if (res.payload) {
                this.setState({ packageSettlementList: res.payload, pendingResponse: false });
            }
        }
    };

    sortData = (key: string, order: string, subKey?: string, subGrandKey?: string) => {
        let packageSettlementList = this.state.packageSettlementList;
        if (packageSettlementList) {
            if (key.includes('date')) {
                packageSettlementList = packageSettlementList.sort(
                    Sort.compareDate(key, subKey, order)
                );
            } else {
                packageSettlementList = packageSettlementList.sort(
                    Sort.compareValues(key, subKey, order, subGrandKey)
                );
            }
        }

        this.setState({ packageSettlementList: packageSettlementList });
    };

    handleClearSearch = async () => {
        this.setState(
            {
                startDateFilter: undefined,
                endDateFilter: undefined,
                selectedStatuses: [],
                selectedPlaintiffsFirms: [],
            },
            this.loadPackageSettlementList
        );
    };

    delete = async () => {
        if (!this.state.selectedPackageSettlementGuidToDelete || !this.props.zoneContext) return;

        const response = await this.props.zoneContext?.zoneApiClient.deletePackageSettlement(
            this.state.selectedPackageSettlementGuidToDelete
        );
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
            });
            return;
        }

        const packageSettlementList = this.state.packageSettlementList;
        packageSettlementList.splice(
            packageSettlementList.findIndex(
                (x) => x.guid === this.state.selectedPackageSettlementGuidToDelete
            ),
            1
        );
        this.setState({
            packageSettlementList: packageSettlementList,
            showConfirmDeleteDialog: false,
        });
    };

    handleApplyFilters = (searchModel: IPackageSettlementSearchModel) => {
        this.setState(
            {
                startDateFilter: searchModel.startDate,
                endDateFilter: searchModel.endDate,
                selectedStatuses:
                    this.props.statuses?.filter((opt) => searchModel.statusIds?.includes(opt.id)) ??
                    [],
                selectedPlaintiffsFirms:
                    this.props.plaintiffsFirms?.filter((opt) =>
                        searchModel.plaintiffsFirmIds?.includes(opt.value)
                    ) ?? [],
            },
            this.loadPackageSettlementList
        );
    };

    handleStatusFilterChange = (statuses: IMultiSelectOption[]) => {
        this.setState({ selectedStatuses: statuses });
    };

    handlePlaintiffsFirmFilterChange = (firms: IMultiSelectOption[]) => {
        this.setState({ selectedPlaintiffsFirms: firms });
    };

    render() {
        if (!this.props.zoneContext) return <></>;

        const showAddButton = Authorization.userHasRight(
            UserRightsEnum.AddEditDeletePackageSettlement,
            this.props.user,
            this.props.zoneContext.zoneGuid
        );

        return (
            <>
                {this.state.pendingResponse && <Loader />}
                <DocumentTitle title={pageTitle} />
                <Offcanvas
                    show={this.state.showFilterPane}
                    onHide={() => this.setState({ showFilterPane: false })}
                    placement="end"
                    scroll
                    backdrop
                >
                    <Offcanvas.Header
                        closeButton
                        style={{ borderBottom: '1px solid lightgray', height: '77px' }}
                    >
                        <Offcanvas.Title>
                            <span style={{ fontSize: 20, fontWeight: 500 }}>
                                {pageTitle} Filters
                            </span>
                        </Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <PackageListSearchFilters
                            appliedSearchModel={{
                                startDate: this.state.startDateFilter,
                                endDate: this.state.endDateFilter,
                                statusIds: this.state.selectedStatuses?.map((filter) => filter.id),
                                plaintiffsFirmIds: this.state.selectedPlaintiffsFirms.map(
                                    (filter) => filter.value
                                ),
                            }}
                            onApplyFilters={this.handleApplyFilters}
                            isSearching={this.state.pendingResponse}
                        />
                    </Offcanvas.Body>
                </Offcanvas>

                <PageWithAffixedHeader
                    headerComponent={
                        <>
                            <Stack
                                direction="horizontal"
                                style={{ justifyContent: 'space-between' }}
                            >
                                <h1 className="d-inline-block mb-0">Package Deals</h1>
                                {showAddButton && (
                                    <Link
                                        to={this.props.zoneContext.buildZoneUrl(
                                            LocalRoutes.PackageSettlementDetails.replace(
                                                ':guid',
                                                ''
                                            )
                                        )}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <span className="btn-green btn float-end btn-icon">
                                            <i className="fal fa-lg fa-plus color-white" />
                                        </span>
                                    </Link>
                                )}
                            </Stack>
                            <PackageListAppliedFilters
                                searchModel={{
                                    startDate: this.state.startDateFilter,
                                    endDate: this.state.endDateFilter,
                                    statusIds: this.state.selectedStatuses?.map(
                                        (filter) => filter.id
                                    ),
                                    plaintiffsFirmIds: this.state.selectedPlaintiffsFirms.map(
                                        (filter) => filter.value
                                    ),
                                }}
                                onApplyFilters={this.handleApplyFilters}
                                onToggleFilterPane={() => this.setState({ showFilterPane: true })}
                            />
                        </>
                    }
                >
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <SortableHeader
                                    headerText="Name"
                                    sortKey="name"
                                    onSort={this.sortData}
                                    thClassName="col-sm-2"
                                />
                                <SortableHeader
                                    headerText="Status"
                                    sortKey="status"
                                    subKey="displayName"
                                    onSort={this.sortData}
                                />
                                <SortableHeader
                                    headerText="Initial Date"
                                    sortKey="initialDate"
                                    onSort={this.sortData}
                                    noWrap
                                />
                                <SortableHeader
                                    headerText="Final Date"
                                    sortKey="finalDate"
                                    onSort={this.sortData}
                                    noWrap
                                />
                                <SortableHeader
                                    headerText="Plaintiffs Firms"
                                    sortKey="firmSortText"
                                    onSort={this.sortData}
                                    thClassName="col-sm-2"
                                    noWrap
                                />
                                <th className="center">Total Cases</th>
                                <th className="center">Dismissed</th>
                                <th className="center">Settled</th>
                                <th className="center">Alt-Monitored</th>
                                <th className="center">Total Settlement</th>
                                {Authorization.userHasRight(
                                    UserRightsEnum.AddEditDeletePackageSettlement,
                                    this.props.user,
                                    this.props.zoneContext.zoneGuid
                                ) && <th />}
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.packageSettlementList.map(
                                (deal: IPackageSettlementBaseModel) => {
                                    if (!this.props.zoneContext) return <></>;
                                    return (
                                        <tr key={deal.guid}>
                                            <td>
                                                {' '}
                                                <Link
                                                    target="_blank"
                                                    to={this.props.zoneContext.buildZoneUrl(
                                                        LocalRoutes.PackageSettlementDetails.replace(
                                                            ':guid',
                                                            deal!.guid!
                                                        )
                                                    )}
                                                >
                                                    {deal.name}
                                                </Link>
                                            </td>
                                            <td>{deal.status!.displayName}</td>
                                            <td>{Common.dateFormat(deal.initialDate)}</td>
                                            <td>{Common.dateFormat(deal.finalDate)}</td>
                                            <td>
                                                {deal.firms ? (
                                                    <ul className="m-0 list-unstyled">
                                                        {deal.firms.map(
                                                            (firm: IPackageSettlementFirmModel) => {
                                                                return (
                                                                    <li key={firm.guid}>
                                                                        {firm.name}
                                                                    </li>
                                                                );
                                                            }
                                                        )}
                                                    </ul>
                                                ) : null}
                                            </td>
                                            <td className="center">
                                                {!deal.caseStats ? '' : deal.caseStats.total}
                                            </td>
                                            <td className="center">
                                                {!deal.caseStats ? '' : deal.caseStats.dismissed}
                                            </td>
                                            <td className="center">
                                                {!deal.caseStats ? '' : deal.caseStats.settled}
                                            </td>
                                            <td className="center">
                                                {!deal.caseStats ? '' : deal.caseStats.alt}
                                            </td>
                                            <td className="center">
                                                {!deal.caseStats
                                                    ? ''
                                                    : deal.caseStats.settlement
                                                      ? Common.formatCurrency(
                                                            deal.caseStats.settlement.toString()
                                                        )
                                                      : ''}
                                            </td>
                                            {Authorization.userHasRight(
                                                UserRightsEnum.AddEditDeletePackageSettlement,
                                                this.props.user,
                                                this.props.zoneContext.zoneGuid
                                            ) && (
                                                <td>
                                                    <button
                                                        className="btn-no-bg float-end"
                                                        onClick={() => {
                                                            this.setState({
                                                                showConfirmDeleteDialog: true,
                                                                selectedPackageSettlementGuidToDelete:
                                                                    deal.guid!,
                                                            });
                                                        }}
                                                    >
                                                        <i className="fal fa-lg fa-trash-alt"></i>
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                }
                            )}
                        </tbody>
                    </table>
                </PageWithAffixedHeader>
                <CheckmateDialog
                    body="Are you sure you want to delete this item? This operation is permanent and cannot be reverted back."
                    cancelText="Cancel"
                    closeButtonClassName="btn btn-default"
                    confirmButtonClassName="btn btn-black"
                    confirmText="Delete"
                    dialogClassName="confirm-document-delete-dialog"
                    handleClose={() => this.setState({ showConfirmDeleteDialog: false })}
                    handleConfirm={this.delete}
                    isShowingModal={this.state.showConfirmDeleteDialog || false}
                    title="Delete"
                    showBackdrop
                />
            </>
        );
    }
}
